import React, { useContext, useEffect, useState } from "react"
import {
    Alert,
    Box,
    Card,
    CardContent,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import Loading from "../../components/Common/Loading"
import { SnackBarContext } from "../../snackBarProvider"
import { useGetResearchDonationsTOHQuery } from "../../core/api/toh-clans-api"
import { formatDateTime } from "../BossEvents/toh"
import { alpha } from "@mui/material/styles"

function ClanIdField({ value, onChange, error, helperText }) {
    return (
        <TextField
            label="Clan ID" size="small" fullWidth required
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={error}
            helperText={helperText}
        />
    )
}

function UserFilterField({ value, onChange, helperText }) {
    return (
        <TextField
            label="User ID" size="small" fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            helperText={helperText}
        />
    )
}

function PlayerHeader({ player, onCopy }) {
    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
            <Typography
                variant="subtitle2"
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" }, color: "primary.main" }}
                onClick={() => onCopy(player.userId, "User ID")}
            >
                {player.userId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                Exp: {player.totalExpAmount ?? 0} · Cores: {player.totalCoresAmount ?? 0}
            </Typography>
        </Stack>
    )
}

function DonationsTable({ donations, onCopy }) {
    if (!donations || donations.length === 0) {
        return <Typography sx={{ color: "text.secondary", py: 1 }}>No donations for this player</Typography>
    }
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Donation ID</TableCell>
                    <TableCell>Talent ID</TableCell>
                    <TableCell>Exp</TableCell>
                    <TableCell>Cores</TableCell>
                    <TableCell>Pay Type</TableCell>
                    <TableCell>Cheater</TableCell>
                    <TableCell>Created</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {donations.map((d) => (
                    <TableRow
                        key={d.donationId}
                        sx={d.isCheatersDonation ? (theme) => ({ backgroundColor: alpha(theme.palette.error.main, 0.08) }) : undefined}
                    >
                        <TableCell
                            onClick={() => onCopy(d.donationId, "Donation ID")}
                            sx={{ cursor: "pointer" }}
                        >
                            {d.donationId}
                        </TableCell>
                        <TableCell
                            onClick={() => onCopy(d.talentId, "Talent ID")}
                            sx={{ cursor: "pointer" }}
                        >
                            {d.talentId}
                        </TableCell>
                        <TableCell>{d.expAmount}</TableCell>
                        <TableCell>{d.coresAmount}</TableCell>
                        <TableCell>{d.payType}</TableCell>
                        <TableCell>{d.isCheatersDonation ? "true" : "false"}</TableCell>
                        <TableCell>{formatDateTime(d.createdAt)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}

function ResearchesTOH({ defaultClanId = "", deviceId = "" }) {
    const useSnackBars = () => useContext(SnackBarContext)
    const { addAlert } = useSnackBars()

    const [clanId, setClanId] = useState(defaultClanId || "")
    const [clanIdTouched, setClanIdTouched] = useState(false)

    const [userIdFilter, setUserIdFilter] = useState("")
    const [userIdFilterTouched, setUserIdFilterTouched] = useState(false)

    useEffect(() => {
        if (!clanIdTouched) {
            setClanId(defaultClanId || "")
        }
    }, [defaultClanId, clanIdTouched])

    useEffect(() => {
        if (!userIdFilterTouched) {
            setUserIdFilter(deviceId || "")
        }
    }, [deviceId, userIdFilterTouched])

    const trimmedClanId = clanId.trim()
    const trimmedUserId = userIdFilter.trim()
    const hasClanId = !!trimmedClanId
    const skip = !hasClanId

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useGetResearchDonationsTOHQuery(
        { clanId: trimmedClanId, userId: trimmedUserId || undefined },
        { skip }
    )

    const players = data?.players || []

    const handleCopy = async (text, label = "ID") => {
        try {
            await navigator.clipboard.writeText(text)
            addAlert({ text: `${label} copied to clipboard`, type: "success" })
        } catch {
            addAlert({ text: "Failed to copy", type: "error" })
        }
    }

    const handleClanIdChange = (v) => {
        setClanId(v)
        setClanIdTouched(true)
    }

    const handleUserFilterChange = (v) => {
        setUserIdFilter(v)
        setUserIdFilterTouched(true)
    }

    const clanIdError = false
    const clanIdHelper = defaultClanId
        ? "Auto-filled from current player's clan"
        : "Enter a Clan ID to view research donations"

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="h6">Research Donations</Typography>
                    {hasClanId && (
                        <Tooltip title="Reload">
                            <IconButton size="small" onClick={refetch} disabled={isFetching}>
                                <RefreshIcon/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", mb: 2 }}>
                    <ClanIdField
                        value={clanId}
                        onChange={handleClanIdChange}
                        error={clanIdError}
                        helperText={clanIdHelper}
                    />
                </Stack>

                <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", mb: 2 }}>
                    <UserFilterField
                        value={userIdFilter}
                        onChange={handleUserFilterChange}
                        helperText="Leave empty to show all players in the clan"
                    />
                </Stack>

                {!trimmedClanId ? (
                    <Alert severity="info">Enter a Clan ID to view research donations</Alert>
                ) : (
                    <Loading isLoading={isLoading} isError={isError} error={error}>
                        {players.length > 0 ? (
                            players.map((player) => (
                                <Box key={player.userId} sx={{ mb: 3 }}>
                                    <PlayerHeader player={player} onCopy={handleCopy}/>
                                    <DonationsTable donations={player.donations} onCopy={handleCopy}/>
                                </Box>
                            ))
                        ) : (
                            <Typography sx={{ color: "text.secondary", py: 1 }}>
                                No research donation data for this
                                clan{trimmedUserId ? ` and user ${trimmedUserId}` : ""}
                            </Typography>
                        )}
                    </Loading>
                )}
            </CardContent>
        </Card>
    )
}

export default ResearchesTOH
